// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import apiClient from './client';
import type { BrandingConfig, ProviderBrandingResponse } from '../types/branding';

export const brandingApi = {
  /**
   * Get branding configuration for a provider's brokerage
   * @param providerCode - Provider code (extracted from subdomain)
   * @returns Resolved branding configuration with logo, tier, badge flag, and custom styles
   */
  getBrandingConfig: async (providerCode: string): Promise<BrandingConfig> => {
    const response = await apiClient.get<BrandingConfig>(`/api/branding/config/${providerCode}`);
    return response.data;
  },

  /**
   * Get public branding for a provider portal (no auth required).
   * Used on the login page before authentication.
   *
   * Reads the Provider Server's portal configuration (`/api/config/branding`,
   * proxied unauthenticated by provider-bff). branding-service only holds
   * brokerage tenants, so `/api/branding/public/subdomain/{code}` always 404s
   * for a provider code.
   * @param providerCode - Provider code (extracted from subdomain)
   * @returns Branding mapped onto BrandingConfig, with the provider's display name
   */
  getPublicBranding: async (providerCode: string): Promise<BrandingConfig> => {
    const response = await apiClient.get<ProviderBrandingResponse>('/api/config/branding', {
      params: { providerCode },
    });
    const { logoUrl, displayName, providerName, brandingConfig } = response.data;
    return {
      ...(brandingConfig ?? {}),
      logoUrl: logoUrl ?? '',
      brandingTier: 'NONE',
      showPoweredByBadge: true,
      displayName: displayName || providerName || undefined,
    };
  },

  /**
   * Get logo URL for a provider's brokerage
   * @param providerCode - Provider code (extracted from subdomain)
   * @returns Logo URL based on branding tier
   */
  getLogoUrl: async (providerCode: string): Promise<string> => {
    const response = await apiClient.get<{ logoUrl: string }>(`/api/branding/logo/${providerCode}`);
    return response.data.logoUrl;
  }
};
